import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as XLSX from 'xlsx';
import { getUteUploadUrl, processUteUpload, getUploadJobStatus } from '../../services/api';

const ACCEPTED_TYPES = {
  'text/csv': '.csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-excel': '.xls',
  'application/x-iwork-keynote-sffnumbers': '.numbers',
};

const STEPS = [
  { key: 'select', label: 'Select File' },
  { key: 'uploading', label: 'Uploading' },
  { key: 'processing', label: 'Processing' },
  { key: 'complete', label: 'Complete' },
];

const MONTH_NAMES = [
  '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/* ── Component ───────────────────────────────────────────── */

function UteImport({ onNavigate }) {
  const [step, setStep] = useState('select');
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const [calendarYear, setCalendarYear] = useState('2026');
  const [detectedInfo, setDetectedInfo] = useState(null);
  const [result, setResult] = useState(null);
  const [jobId, setJobId] = useState(null);
  const fileInputRef = useRef(null);

  /* ── Validation ──────────────────────────────────── */

  const validateFile = useCallback((f) => {
    if (!f) return 'No file selected';
    const ext = f.name.toLowerCase().split('.').pop();
    const validExts = ['csv', 'xlsx', 'xls', 'numbers'];
    if (!ACCEPTED_TYPES[f.type] && !validExts.includes(ext)) {
      return 'Invalid file type. Only CSV and spreadsheet files (.csv, .xlsx, .xls, .numbers) are accepted.';
    }
    if (f.size > 10 * 1024 * 1024) {
      return 'File too large. Maximum size is 10MB.';
    }
    return null;
  }, []);

  /* ── Auto-detect info from uploaded file ──────────── */

  const detectInfo = useCallback((f) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });

        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });

        if (rows.length < 2) {
          setDetectedInfo(null);
          return;
        }

        const headers = rows[0].map((h) => String(h || '').trim().toLowerCase());
        const dataRows = rows.slice(1).filter((r) => r.some((c) => c !== ''));
        const rowCount = dataRows.length;

        // Find employee name column
        const nameIdx = headers.findIndex((h) =>
          h === 'name' || h === 'employee' || h === 'person' || h === 'employee name' || h === 'person name'
        );
        let employeeCount = null;
        if (nameIdx >= 0) {
          const names = new Set();
          dataRows.forEach((r) => {
            const val = String(r[nameIdx] || '').trim();
            if (val) names.add(val);
          });
          employeeCount = names.size;
        }

        // Find date column and detect range
        const dateIdx = headers.findIndex((h) =>
          h === 'date' || h === 'work date' || h === 'workdate' || h === 'time date' || h === 'period date'
        );
        let dateRange = null;
        if (dateIdx >= 0) {
          let minDate = null;
          let maxDate = null;
          dataRows.forEach((r) => {
            const raw = r[dateIdx];
            if (!raw) return;
            const d = new Date(raw);
            if (isNaN(d.getTime())) return;
            if (!minDate || d < minDate) minDate = d;
            if (!maxDate || d > maxDate) maxDate = d;
          });
          if (minDate && maxDate) {
            const fmt = (d) => `${MONTH_NAMES[d.getMonth() + 1]} ${d.getDate()}, ${d.getFullYear()}`;
            dateRange = `${fmt(minDate)} \u2013 ${fmt(maxDate)}`;
          }
        }

        setDetectedInfo({ rowCount, employeeCount, dateRange });
      } catch {
        setDetectedInfo(null);
      }
    };
    reader.readAsArrayBuffer(f);
  }, []);

  /* ── File handling ────────────────────────────────── */

  const handleFile = useCallback((f) => {
    const err = validateFile(f);
    if (err) {
      setError(err);
      return;
    }
    setError('');
    setFile(f);
    detectInfo(f);
  }, [validateFile, detectInfo]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, [handleFile]);

  const handleInputChange = useCallback((e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  }, [handleFile]);

  /* ── Upload flow ──────────────────────────────────── */

  // Poll for job completion when in processing state
  useEffect(() => {
    if (step !== 'processing' || !jobId) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const status = await getUploadJobStatus('delivery', jobId);
        if (cancelled) return;

        if (status.status === 'COMPLETE') {
          setResult(status);
          setStep('complete');
        } else if (status.status === 'ERROR') {
          setError(status.error || 'Processing failed');
          setStep('select');
        }
        // PROCESSING → keep polling
      } catch {
        // Ignore transient fetch errors, keep polling
      }
    };

    const interval = setInterval(poll, 2000);
    poll(); // immediate first check
    return () => { cancelled = true; clearInterval(interval); };
  }, [step, jobId]);

  async function handleUpload() {
    if (!file) return;
    setError('');

    try {
      // Step 1: Get presigned URL
      setStep('uploading');
      let uploadUrl, s3Key;
      try {
        ({ uploadUrl, s3Key } = await getUteUploadUrl(file.name, file.type));
      } catch (e) {
        throw new Error(`Step 1 (get upload URL): ${e.message}`);
      }

      // Step 2: Upload to S3
      try {
        const response = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });

        if (!response.ok) {
          const text = await response.text().catch(() => '');
          throw new Error(`S3 returned ${response.status}: ${text.slice(0, 200)}`);
        }
      } catch (e) {
        if (e.message.startsWith('S3 returned')) throw e;
        throw new Error(`Step 2 (S3 upload): ${e.message}`);
      }

      // Step 3: Trigger backend processing (returns 202 immediately)
      setStep('processing');
      try {
        const { jobId: id } = await processUteUpload({
          s3Key,
          year: calendarYear,
        });

        setJobId(id);
        // Polling useEffect will pick up from here
      } catch (e) {
        throw new Error(`Step 3 (process upload): ${e.message}`);
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
      setStep('select');
    }
  }

  function handleReset() {
    setStep('select');
    setFile(null);
    setError('');
    setResult(null);
    setJobId(null);
    setDetectedInfo(null);
  }

  const currentStepIdx = STEPS.findIndex((s) => s.key === step);

  /* ── Render ────────────────────────────────────────── */
  return (
    <div className="form-page">
      <div className="form-card">
        <h2 style={{
          fontFamily: 'Kode Mono, monospace',
          fontSize: '18px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '1px',
          marginBottom: '24px',
          color: 'var(--bcore-text)',
        }}>
          UTE Import
        </h2>

        {/* Progress Steps */}
        <div className="upload-steps">
          {STEPS.map((s, idx) => (
            <div
              key={s.key}
              className={`upload-step${idx <= currentStepIdx ? ' active' : ''}${idx < currentStepIdx ? ' completed' : ''}`}
            >
              <div className="upload-step-dot">
                {idx < currentStepIdx ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <span>{idx + 1}</span>
                )}
              </div>
              <span className="upload-step-label">{s.label}</span>
              {idx < STEPS.length - 1 && <div className="upload-step-line" />}
            </div>
          ))}
        </div>

        {/* Select File State */}
        {step === 'select' && (
          <>
            {/* Upload Options */}
            <div className="form-section" style={{ marginBottom: '20px' }}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Calendar Year <span className="required">*</span></label>
                  <select
                    className="form-input"
                    value={calendarYear}
                    onChange={(e) => setCalendarYear(e.target.value)}
                  >
                    <option value="2025">CY2025</option>
                    <option value="2026">CY2026</option>
                    <option value="2027">CY2027</option>
                  </select>
                </div>

                {detectedInfo && detectedInfo.rowCount > 0 && (
                  <div className="form-group">
                    <label className="form-label">Detected from File</label>
                    <div className="form-input" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'default',
                      opacity: 0.9,
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--bcore-accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span>
                        {detectedInfo.rowCount.toLocaleString()} rows
                        {detectedInfo.employeeCount && (
                          <> &middot; {detectedInfo.employeeCount} employees</>
                        )}
                      </span>
                    </div>
                  </div>
                )}

                {detectedInfo && detectedInfo.dateRange && (
                  <div className="form-group">
                    <label className="form-label">Date Range</label>
                    <div className="form-input" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'default',
                      opacity: 0.9,
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--bcore-accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      {detectedInfo.dateRange}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Dropzone */}
            <div
              className={`upload-dropzone${dragActive ? ' drag-active' : ''}${file ? ' has-file' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls,.numbers,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={handleInputChange}
                style={{ display: 'none' }}
              />
              {file ? (
                <div className="upload-file-info">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <line x1="10" y1="9" x2="8" y2="9" />
                  </svg>
                  <div className="upload-file-name">{file.name}</div>
                  <div className="upload-file-size">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                  <button
                    className="upload-file-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      setDetectedInfo(null);
                    }}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="upload-dropzone-content">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  <div className="upload-dropzone-text">
                    Drag and drop your Unanet time export here
                  </div>
                  <div className="upload-dropzone-hint">
                    or click to browse &bull; .csv, .xlsx, .xls, .numbers &bull; Max 10MB
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="form-api-error">{error}</div>
            )}

            <div className="form-actions">
              <button
                className="btn btn-primary"
                onClick={handleUpload}
                disabled={!file}
              >
                Upload &amp; Process
              </button>
            </div>
          </>
        )}

        {/* Uploading State */}
        {step === 'uploading' && (
          <div className="upload-processing">
            <div className="upload-spinner" />
            <div className="upload-processing-title">Uploading Time Data</div>
            <div className="upload-processing-sub">
              Transferring {file?.name} to secure storage...
            </div>
          </div>
        )}

        {/* Processing State */}
        {step === 'processing' && (
          <div className="upload-processing">
            <div className="upload-spinner" />
            <div className="upload-processing-title">Processing Utilization Data</div>
            <div className="upload-processing-sub">
              Parsing time data and computing utilization metrics...
            </div>
          </div>
        )}

        {/* Complete State */}
        {step === 'complete' && (
          <div className="upload-processing">
            <div className="upload-complete-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <div className="upload-processing-title">Import Complete</div>
            <div className="upload-processing-sub">
              Utilization data has been processed successfully.
            </div>
            {result && (
              <div style={{
                marginTop: '16px',
                padding: '16px',
                background: 'var(--bcore-hover-subtle)',
                borderRadius: '8px',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '12px',
                color: 'var(--bcore-text-muted)',
                textAlign: 'left',
                maxWidth: '360px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}>
                {result.records_written !== undefined && (
                  <div style={{ marginBottom: '6px' }}>
                    <span style={{ color: 'var(--bcore-text-dim)' }}>Records Written:</span>{' '}
                    <span style={{ color: 'var(--bcore-accent)' }}>{result.records_written}</span>
                  </div>
                )}
                {result.employees_processed !== undefined && (
                  <div style={{ marginBottom: '6px' }}>
                    <span style={{ color: 'var(--bcore-text-dim)' }}>Employees Processed:</span>{' '}
                    <span style={{ color: 'var(--bcore-accent)' }}>{result.employees_processed}</span>
                  </div>
                )}
                {result.period && (
                  <div style={{ marginBottom: '6px' }}>
                    <span style={{ color: 'var(--bcore-text-dim)' }}>Period:</span>{' '}
                    <span style={{ color: 'var(--bcore-accent)' }}>{result.period}</span>
                  </div>
                )}
                {result.batch_id && (
                  <div>
                    <span style={{ color: 'var(--bcore-text-dim)' }}>Batch ID:</span>{' '}
                    <span style={{ opacity: 0.6 }}>{result.batch_id}</span>
                  </div>
                )}
              </div>
            )}
            <div className="form-actions" style={{ justifyContent: 'center', marginTop: '20px' }}>
              <button className="btn btn-outline" onClick={handleReset}>
                Upload Another
              </button>
              <button className="btn btn-primary" onClick={() => onNavigate('delivery-dashboard')}>
                View Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

UteImport.propTypes = {
  onNavigate: PropTypes.func.isRequired,
};

export default UteImport;
